import { cn, DateTile } from '@h2hspark/ui';
import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

/**
 * **One row of a Your Events list** — the prototype's *compact event row* (`Web App.dc.html`
 * lines 374–378 and 390–394; its inventory's §3.7), shared by *My upcoming events* and *My follow
 * up* so the two columns cannot disagree about what a row is.
 *
 * `--surface-card`, a hairline `--border-subtle`, `--radius-lg`, `--shadow-xs`, `padding:16px`,
 * `gap:13px`: the 46px list tile (fuchsia for upcoming, purple for follow-up), the name at 700
 * `var(--text-md)`, the date and city at `var(--text-sm)` in `--text-muted`, a pill of 700
 * `var(--text-2xs)` with a 12px icon, and a chevron. The whole row is the link, as the prototype's
 * whole row is the click target — a `Link` rather than its clickable `div`, so it has a focus stop
 * and a name (`handoff-corrections.md` #132).
 */
export type EventRowTone = 'upcoming' | 'follow-up';

/** The pill's three looks, from the prototype: neutral, brand (something to do), success (done). */
export type EventRowPillTone = 'neutral' | 'brand' | 'success';

export function EventRowCard({
  to,
  tone,
  name,
  startsAt,
  timezone,
  when,
  pill,
}: {
  readonly to: string;
  readonly tone: EventRowTone;
  readonly name: string;
  readonly startsAt: string | null;
  readonly timezone: string | null;
  readonly when: string | null;
  readonly pill: {
    readonly label: string;
    readonly tone: EventRowPillTone;
    readonly Icon: LucideIcon;
  };
}): React.ReactElement {
  return (
    <Link
      to={to}
      className="flex items-start gap-[13px] rounded-lg border border-border-subtle bg-card p-4 shadow-xs transition-shadow hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
    >
      {startsAt === null ? (
        <span className="w-[46px] flex-none" aria-hidden />
      ) : (
        <DateTile variant={tone} startsAt={startsAt} timezone={timezone} />
      )}
      <span className="min-w-0 flex-1">
        <span className="block font-brand text-md font-bold text-foreground">{name}</span>
        {when === null ? null : <span className="block text-sm text-muted-foreground">{when}</span>}
        <span
          className={cn(
            'mt-2 inline-flex items-center gap-1.5 rounded-pill px-[11px] py-[5px] font-brand text-2xs font-bold',
            pill.tone === 'brand'
              ? 'bg-brand-50 text-primary'
              : pill.tone === 'success'
                ? 'bg-success-subtle text-success'
                : 'bg-muted text-muted-foreground',
          )}
        >
          <pill.Icon className="h-3 w-3" aria-hidden />
          {pill.label}
        </span>
      </span>
      <ChevronRight className="h-[18px] w-[18px] flex-none text-text-subtle" aria-hidden />
    </Link>
  );
}
